import React, { FC, useCallback, useState } from "react";
import {
  startImpersonating,
  ViewAsSource,
} from "@src/features/app/hooks/useImpersonation";
import { IMPERSONATION_SOFT } from "@src/features/app/impersonation/impersonationStyles";
import EyeOutlineIcon from "@src/lib/icons/18px/eye-outline";
import { Button } from "@src/lib/ui/Button";
import { Checkbox } from "@src/lib/ui/Checkbox";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@src/lib/ui/Dialog";
import { IconTile } from "@src/lib/ui/IconTile";
import { Label } from "@src/lib/ui/Label";
import {
  getFirstName,
  getImpersonatedUserFromUrl,
  getImpersonationReadOnlyMessage,
} from "@src/lib/utils/impersonation";
import { addImpersonationRefusalToast } from "@src/features/app/utils/toast";
import { useBooleanLocalStorage } from "@src/lib/hooks/useLocalStorage";

export type ViewAsTarget = {
  id: number;
  name: string;
  userType: string;
};

type StartOptions = Parameters<typeof startImpersonating>[2];

/** The entry point's label: "View as Maria". */
export const viewAsLabel = (name: string): string =>
  `View as ${getFirstName(name)}`;

/**
 * Starts viewing as someone, showing the explainer first until the viewer
 * ticks "Don't show again" (saved in this browser). Render `dialog` next to
 * the entry point.
 */
export const useViewAs = (
  source: ViewAsSource,
): {
  viewAs: (target: ViewAsTarget, options?: StartOptions) => void;
  dialog: React.ReactNode;
} => {
  const [pending, setPending] = useState<{
    target: ViewAsTarget;
    options?: StartOptions;
  } | null>(null);
  const [starting, setStarting] = useState(false);
  // "Don't show again", per browser.
  const [dismissed, setDismissed] = useBooleanLocalStorage(
    "view-as-confirm-dismissed",
  );

  const viewAs = useCallback(
    (target: ViewAsTarget, options?: StartOptions) => {
      const current = getImpersonatedUserFromUrl();
      if (current) {
        // No view inside a view: exit first.
        addImpersonationRefusalToast(
          getImpersonationReadOnlyMessage(current.name),
        );
        return;
      }
      if (dismissed) {
        void startImpersonating(target, source, options);
      } else {
        setPending({ target, options });
      }
    },
    [dismissed, source],
  );

  const confirm = (dontShowAgain: boolean) => {
    if (!pending) return;
    if (dontShowAgain) setDismissed(true);
    setStarting(true);
    // Resolves only if the page stayed (a beforeunload "Stay"): let the
    // dialog be closed or confirmed again.
    void startImpersonating(pending.target, source, pending.options).then(() =>
      setStarting(false),
    );
  };

  const dialog = pending ? (
    <ViewAsDialog
      open
      onOpenChange={(open) => {
        // Once starting, the page is about to leave: no backing out.
        if (!open && !starting) setPending(null);
      }}
      name={pending.target.name}
      starting={starting}
      onConfirm={confirm}
    />
  ) : null;

  return { viewAs, dialog };
};

type Props = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  name: string;
  starting: boolean;
  onConfirm: (dontShowAgain: boolean) => void;
};

const ViewAsDialog: FC<Props> = ({
  open,
  onOpenChange,
  name,
  starting,
  onConfirm,
}) => {
  const [dontShowAgain, setDontShowAgain] = useState(false);
  const firstName = getFirstName(name);
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent hideCloseButton className="max-w-md">
        <DialogHeader>
          <IconTile size="lg" className={IMPERSONATION_SOFT}>
            <EyeOutlineIcon />
          </IconTile>
          <DialogTitle>View as {name}?</DialogTitle>
        </DialogHeader>
        <DialogDescription>
          {`See Opus exactly as ${firstName} does. It's read-only, and includes their direct messages. Reading doesn't mark anything as read.`}
        </DialogDescription>
        <DialogFooter className="items-center sm:justify-between">
          {/* The whole row toggles the box, at least 44px tall. */}
          <div className="flex min-h-11 items-center gap-2">
            <Checkbox
              id="view-as-dont-show-again"
              checked={dontShowAgain}
              onCheckedChange={(checked) => setDontShowAgain(checked === true)}
            />
            <Label
              htmlFor="view-as-dont-show-again"
              className="flex min-h-11 cursor-pointer items-center font-normal select-none"
            >
              Don&apos;t show again
            </Label>
          </div>
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              disabled={starting}
              onClick={() => onOpenChange(false)}
            >
              Cancel
            </Button>
            <Button
              variant="purple"
              loading={starting}
              onClick={() => onConfirm(dontShowAgain)}
            >
              {viewAsLabel(name)}
            </Button>
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
